import { useEffect, useRef } from "react";
import { messageSeen } from "../../../api/secure-messaging-api";
import { formatDateAndTime } from "../../../constants/date";
import translate from "../../../constants/translations";
import type { CustomerContext, Message } from "../../../types";
import FileAttachmentsList from "../../file-attachments/file-attachments-list";
import HTMLMessageBody from "./html-message-body";
import NewMessageIndicator from "./new-message-indicator";
import PlainTextMessageBody from "./plain-text-message-body";

interface MessageContentProps {
	customer: CustomerContext;
	cultureCode: string;
	message: Message;
	focused: boolean;
	expanded: boolean;
}

export default function MessageContent({
	customer,
	cultureCode,
	message,
	focused,
	expanded,
}: MessageContentProps) {
	const scrolledIntoToViewIfNewest = useRef<HTMLDivElement | null>(null);
	const t = (term: string) => translate(cultureCode, term);

	useEffect(() => {
		if (expanded && !message.seenByCustomer) {
			messageSeen(customer, message.messageId);
		}
	}, [customer, message.messageId, message.seenByCustomer, expanded]);

	useEffect(() => {
		if (focused && expanded && scrolledIntoToViewIfNewest.current) {
			const boundingClientRect =
				scrolledIntoToViewIfNewest.current.getBoundingClientRect();
			const centerOfTheWindow =
				boundingClientRect.top + window.pageYOffset - window.innerHeight / 2;
			const isModernBrowser = window.visualViewport !== undefined;
			if (isModernBrowser) {
				window.scrollTo({ top: centerOfTheWindow, behavior: "smooth" });
			} else {
				window.scrollTo(0, centerOfTheWindow);
			}
		}
	}, [focused, expanded]);

	const sender =
		message.sender === "Customer" ? customer.name : t(message.sender);

	return (
		<div>
			<div className="if grid across" ref={scrolledIntoToViewIfNewest}>
				<div className="if row">
					<div className="if col-9--smlr">
						<div className="mypages-insurance-block">
							<div className="mypages-insurance-field">
								<div className="mypages-insurance-field__label">{`${t("Sender")}`}</div>
								<div className="mypages-insurance-field__value">
									<span id="sender">{` ${sender || ""}`}</span>
									{message.sender === "Frontliner" && (
										<NewMessageIndicator
											seenByCustomer={!!message.seenByCustomer}
											cultureCode={cultureCode}
										/>
									)}
								</div>
							</div>
							<div className="mypages-insurance-field mypages-insurance-field--hidden-up-smlr">
								<div className="mypages-insurance-field__label">{`${t("SentDate")}`}</div>
								<div
									data-test="timestamp"
									className="mypages-insurance-field__value"
								>
									{` ${formatDateAndTime(message.timestamp, cultureCode)}`}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className="if row u-margin-top--small">
				<div
					className={`if col-10--sm ${message.sender === "Customer" ? "secure-messaging-component__customer-reply" : ""}`}
				>
					{message.sender === "Customer" ? (
						<PlainTextMessageBody>{message.body}</PlainTextMessageBody>
					) : (
						<HTMLMessageBody>{message.body}</HTMLMessageBody>
					)}
				</div>
			</div>
			<FileAttachmentsList
				cultureCode={cultureCode}
				customer={customer}
				attachments={message.attachments}
			/>
			<hr className="mypages-offers-messages__separator" />
		</div>
	);
}
